import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function TrustOverview() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2" style={{ color: 'var(--fy-text-secondary)' }} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-10">
          <Shield className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }} data-testid="page-title">
            Trust & Safety
          </h1>
        </div>

        <div className="space-y-10">

          {/* Intro — lead with how the system is designed */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-light)', border: '1px solid var(--lk-sage)' }}>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind is designed so safety comes from structure, not moderation after the fact.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              The system is built around activities, not profiles or content.
            </p>
          </section>

          {/* System Design — framed as "how it works," not "what we block" */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              How the System is Designed
            </h2>
            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Activities are shown by time and location</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Nothing is ranked or promoted based on engagement.</p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Coordination happens through signals</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Interaction is structured around activities — not open conversation.</p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Participation is optional</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>There are no scores, streaks, or systems that pressure you to keep going.</p>
              </div>
            </div>
          </section>

          {/* Child Safety — clear and firm */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Child Safety
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              The system is designed so child accounts are:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                'Not publicly discoverable',
                'Always under parent visibility (under 13)',
                'Restricted from direct peer messaging',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              These protections are built into the system and cannot be bypassed.
            </p>
            <Link to="/parent-visibility" className="inline-block mt-3 text-sm underline" style={{ color: 'var(--lk-sage)' }}>
              Parent Visibility & Child Safety
            </Link>
          </section>

          {/* Enforcement — simple and direct */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Enforcement
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Content or behavior that creates risk may be removed.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Child safety violations result in immediate action.
            </p>
          </section>

          {/* Standard — closing */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind reduces risk through structure, visibility, and constraints.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Trust is created by how the system works, not what it promises.
            </p>
          </section>

          {/* Related */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex flex-wrap gap-3">
              {[
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/parent-visibility', label: 'Parent Visibility' },
                { to: '/terms', label: 'Terms of Service' },
              ].map(link => (
                <Link key={link.to} to={link.to} className="px-4 py-2 rounded-full text-sm" style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}>
                  {link.label}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>© {new Date().getFullYear()} LikeKind Inc.</p>
        </div>
      </footer>
    </div>
  );
}
